<template>
  <div class="flex justify-between gap-4 py-2">
    <a
      v-if="isAvailable"
      class="w-3/4 truncate text-blue-600 underline"
      target="_blank"
      rel="noopener noreferrer"
      :href="documentEndpoint"
    >
      {{ document.documentName }}
    </a>
    <span v-else class="w-3/4 truncate text-gray-500">
      {{ document.documentName }}
    </span>
    <a
      v-if="isAvailable"
      :download="document.documentName"
      :href="documentEndpoint"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="mr-4 h-6 w-6"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
        />
      </svg>
    </a>
    <span v-else class="mr-4 shrink-0 text-right text-sm text-gray-500">
      {{ statusMessage }}
    </span>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import {
    type DocumentMeta,
    getSiteDocumentMeta,
    VerifiedDocument,
  } from '@/services/api/site'
  import { documentContentUrl } from '@/services/api/documentContent'
  import { getCustomerDocumentMeta } from '@/services/api/customer'
  import { type DocumentStatus, documentStatusMessage } from '@/types/documents'
  import InvalidStoreException from '@/exceptions/InvalidStoreException'
  import { storeToRefs } from 'pinia'
  import { useCustomerStore } from '@/storage/customer'
  import { useSiteStore } from '@/storage/site'
  import Log from '@/utilities/Log'

  export default defineComponent({
    name: 'DocumentListItem',

    props: {
      document: {
        type: Object as PropType<VerifiedDocument>,
        required: true,
      },

      documentType: {
        type: String,
        required: true,
      },
    },

    async setup(props) {
      const { id: siteId, isSiteStoreDefault } = storeToRefs(useSiteStore())
      const { id: customerId, isCustomerStoreDefault } =
        storeToRefs(useCustomerStore())
      const meta = await getDocumentMeta()
      const status: DocumentStatus = meta?.status ?? 'missing'
      const isAvailable = status === 'available' && meta !== null
      const documentEndpoint = isAvailable
        ? documentContentUrl(meta)
        : undefined

      // No evacuation diagram or induction reaches this view, so the generic wording applies.
      const statusMessage = documentStatusMessage(status)

      async function getDocumentMeta(): Promise<DocumentMeta | null> {
        if (props.documentType === 'site') {
          if (isSiteStoreDefault.value) {
            throw new InvalidStoreException()
          }

          return await orMissing(() =>
            getSiteDocumentMeta(siteId.value, props.document.id),
          )
        }

        if (props.documentType === 'customer') {
          if (isCustomerStoreDefault.value) {
            throw new InvalidStoreException()
          }

          return await orMissing(() =>
            getCustomerDocumentMeta(customerId.value, props.document.id),
          )
        }

        return null
      }

      async function orMissing(
        read: () => Promise<DocumentMeta>,
      ): Promise<DocumentMeta | null> {
        try {
          return await read()
        } catch (error) {
          Log.notify(error as Error, 'Unreadable document meta')

          return null
        }
      }

      return {
        documentEndpoint,
        isAvailable,
        statusMessage,
      }
    },
  })
</script>
